import { Component, Input, ViewChild, OnInit, OnDestroy } from '@angular/core';

import { Observable, Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { Tree } from 'primeng/tree';
import { TreeNode } from 'primeng/api';

import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { DataSelectDialogServiceInterface } from '../../services/data-select-dialog.service.interface';

import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';

import { DataBrowserType } from '../../models/data-select-dialog.model';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';

import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { AgprComponentStyleEnum } from '../../../Basic/enums/social-analysis-types.enum';

/**
 * Component that display a dialog to select from a list of data options.
 */
@Component({
    selector: 'agpr-data-select-takeover-panel',
    templateUrl: './data-select-takeover-panel.component.html',
    styleUrls: ['./data-select-takeover-panel.component.scss'],
    standalone: false
})
export class DataSelectTakeoverPanelComponent extends AbstractDialogComponent implements OnInit, OnDestroy {

    @Input() id: string;
    @Input() dialogHeader: string;
    @Input() dialogMessage: string;
    @Input() leftColumnHeader: string;
    @Input() rightColumnHeader: string;

    @Input() set dialogService(service: DataSelectDialogServiceInterface) {
        if (service) {
            this.dataSelectDialogService = service;
        }
    }

    @Input() filterDataType: DataBrowserType = null;
    @Input() set initialFilterString(query: string) {
        if (query) {
            this.filterString = query;
        }
    }

    /** Takeover planel related props */
    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;

    /** Tree list related props*/
    @ViewChild('tree') tree: Tree;
    @Input() lockMessage: string = 'Your account does not have access to this';
    @Input() propagateSelection: boolean = true;
    @Input() style: AgprComponentStyleEnum = AgprComponentStyleEnum.default;



    hideSlideOverlay: boolean = false;

    dataSelectDialogService: DataSelectDialogServiceInterface;
    subscriptions: Subscription[] = [];
    availableItems: TreeNode[] = [];
    selectedItems: TreeNode[] = [];
    selectedItemsToDisplay: TreeNode[] = [];
    selectedItemTypesMap: Map<any, any>;
    selectedItemTypes = [];

    filterString: string = '';
    filteredAvailableItems: TreeNode[] = [];
    selectedFilterItem: TreeNode<any>;

    readonly buttonTypes = ButtonTypesEnum;
    readonly agprComponentStyle = AgprComponentStyleEnum;

    constructor(
        public coreDialogService: CoreDialogService,
    ) {
        super(coreDialogService, 'DataSelect');
    }

    ngOnInit() {
        if (this.dataSelectDialogService) {
            this.subscriptions.push(
                this.dataSelectDialogService.getListItems()
                    .pipe(filter(x => !!x))
                    .subscribe(availableListItems => {
                        this.availableItems = availableListItems;
                        if (this.filterDataType) {
                            // Ensure all items start showing, and collapsed
                            // Sorting the children of the filteredAvailableItems
                            this.filteredAvailableItems = this.resetAllItems(this.availableItems).map(filterItemParent => {
                                filterItemParent.children?.sort((a, b) => {
                                    if (a.label < b.label) {
                                        return -1;
                                    }
                                    if (a.label > b.label) {
                                        return 1;
                                    }
                                    return 0;
                                });
                                return filterItemParent;
                            });
                        }
                    }),
                this.dataSelectDialogService.getSelectedListItems()
                    .pipe(filter(x => !!x))
                    .subscribe(selectedListItems => {
                        this.selectedItems = selectedListItems.slice();
                    }),
                this.dataSelectDialogService.getSelectedItemsToDisplay()
                    .pipe(filter(x => !!x))
                    .subscribe(selectedDisplayItems => {
                        this.selectedItemsToDisplay = selectedDisplayItems.slice();
                    }),
                this.dataSelectDialogService.getSelectedItemTypes()
                    .pipe(filter(x => !!x))
                    .subscribe(selectedItemTypesMap => {
                        this.selectedItemTypes = [];
                        this.selectedItemTypesMap = selectedItemTypesMap;
                        this.selectedItemTypes.push(...this.selectedItemTypesMap.keys());
                    })
            );

            if (this.filterString) {
                this.filterItems();
            }
        }
    }


    /**
     * Filtering available items
     */
    filterItems() {
        if (this.filterString.length > 2) {
            this.dataSelectDialogService.filterItems(this.filterString)
                .pipe(take(1))
                .subscribe(filteredItems => {
                    this.filteredAvailableItems = filteredItems;
                });
        } else {
            this.filteredAvailableItems = this.resetAllItems(this.availableItems);
        }
    }

    private resetAllItems(items: TreeNode[]) {
        return items.map(item => {
            item.styleClass = '';
            item.expanded = false;
            item.children?.forEach(child => child.styleClass = '');
            return item;
        });
    }

    /**
     * Handler for expanding a node
     */
    nodeExpanded(event: any) {
        this.dataSelectDialogService.nodeExpanded(event.node, this.propagateSelection);
    }

    /**
     * Handler for collapsing a node
     */
    nodeCollapsed(event: any) {
        this.dataSelectDialogService.nodeCollapsed(event.node);
    }

    /**
     * Handler for selection changes
     */
    selectionChange(event: any) {
        this.dataSelectDialogService.selectionChange(event, this.availableItems, this.propagateSelection);
    }

    /**
     * Handles items being selected
     */
    itemSelected(event: any) {
        this.dataSelectDialogService.itemSelected(event.node);
    }

    /**
     * Handles items being unselected
     */
    itemUnselected(event: any) {
        this.dataSelectDialogService.itemRemoved(event.node, this.propagateSelection);
    }

    /**
     * Removes item from selected item list
     *
     * @param index position of item in selectedItems
     */
    removeItem(item: TreeNode) {
        this.dataSelectDialogService.displayItemDeleted(item, this.propagateSelection);
    }

    /**
     * Reset the selections
     */
    reset() {
        this.dataSelectDialogService.reset();
    }

    /**
     * apply the selections and close the panel
     */
    applySelected() {
        this.dataSelectDialogService.applySelectedListItems(this.selectedItems, this.propagateSelection);
        this.close();
    }


    close(): void {
        this.hideSlideOverlay = true;
    }

    /**
     * Handler for closing the panel
     */
    handleVisibleChange(isVisible: boolean) {
        if(!isVisible){
            this.selectedItems = [];
            this.closeDialog(this.dataSelectDialogService.getSelectedItems());
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
